import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { ReactNode } from 'react';
import { IconClose } from './icons';
import { cn, focusRing } from './utils';

export interface PopoverProps {
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  readonly title?: string;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
  readonly align?: 'start' | 'center' | 'end';
  readonly width?: string;
  readonly showClose?: boolean;
}

/**
 * Popover não modal (o fundo continua interativo).
 *
 * Ao contrário do tooltip, o popover recebe FOCO e pode conter controles —
 * é o que usamos para o painel de filtros. O Radix devolve o foco ao gatilho no
 * fechamento e fecha com Esc, que é o que o padrão APG exige de um dialog não
 * modal.
 */
export function Popover({
  trigger,
  children,
  title,
  open,
  onOpenChange,
  side = 'bottom',
  align = 'start',
  width = '20rem',
  showClose = false,
}: PopoverProps) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={12}
          style={{ width }}
          className={cn(
            'z-dropdown rounded-lg border border-border bg-surface-raised p-4 shadow-lg',
            'animate-content-in',
          )}
        >
          {title ? (
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-content">{title}</h2>
              {showClose ? (
                <PopoverPrimitive.Close
                  aria-label="Fechar"
                  className={cn(
                    'rounded p-1 text-content-subtle hover:bg-surface-sunken hover:text-content',
                    focusRing,
                  )}
                >
                  <IconClose size={15} />
                </PopoverPrimitive.Close>
              ) : null}
            </div>
          ) : null}
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
